import React, { useState, useRef, useCallback } from 'react';
import api from '@/lib/api';
import {
  Upload, FileText, AlertTriangle, CheckCircle, Loader2, X,
  ShieldCheck, FolderOpen, Tag, Cog,
} from 'lucide-react';

const MAX_BYTES = 25 * 1024 * 1024;
const ACCEPTED_EXT = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx'];
const ACCEPT_ATTR = '.pdf,.jpg,.jpeg,.png,.doc,.docx,application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const CATEGORIES = [
  { value: 'statements',          label: 'Statements' },
  { value: 'tax_forms',           label: 'Tax Forms' },
  { value: 'loan_documents',      label: 'Loan Documents' },
  { value: 'legal_rbco',          label: 'Legal / RBCO' },
  { value: 'beneficiary',         label: 'Beneficiary' },
  { value: 'withdrawal_support',  label: 'Withdrawal Support' },
  { value: 'personnel_records',   label: 'Personnel Records' },
  { value: 'uncategorized',       label: 'Uncategorized' },
];

const niceSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};


/**
 * DocumentUploadZone — drag-and-drop + click-to-browse with real XHR progress.
 *
 * State machine per file:
 *   queued   → awaiting user to click "Upload"
 *   uploading (0..99%) with live XHR progress
 *   processing (100%, waiting for server response body)
 *   complete (status="under_review") → callback to parent so library refreshes
 *   error (with specific message per spec)
 *
 * Uses raw XMLHttpRequest for authentic upload progress events.
 */
export const DocumentUploadZone = ({ onUploaded }) => {
  const [dragActive, setDragActive] = useState(false);
  const [queued, setQueued] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [sharedCategory, setSharedCategory] = useState('uncategorized');
  const inputRef = useRef(null);

  const onDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const onDragOver  = (e) => { e.preventDefault(); e.stopPropagation(); };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    handleFiles(Array.from(e.dataTransfer?.files || []));
  };
  const onClickBrowse = () => inputRef.current?.click();
  const onInputChange = (e) => {
    handleFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  const handleFiles = (files) => {
    const staged = [];
    for (const f of files) {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const ext = f.name.toLowerCase().split('.').pop() || '';
      let rejectMsg = null;
      if (!ACCEPTED_EXT.includes(ext)) rejectMsg = 'File type not accepted';
      else if (f.size > MAX_BYTES)    rejectMsg = 'File exceeds 25MB limit';
      else if (f.size === 0)          rejectMsg = 'File is empty';
      if (rejectMsg) {
        setUploads(prev => [{
          localId: id, filename: f.name, size: f.size, status: 'error', error: rejectMsg,
        }, ...prev]);
      } else {
        staged.push({ localId: id, file: f, category: sharedCategory });
      }
    }
    if (staged.length) setQueued(prev => [...prev, ...staged]);
  };

  const removeFromQueue = (id) => setQueued(prev => prev.filter(q => q.localId !== id));
  const setItemCategory = (id, cat) => setQueued(prev => prev.map(q => q.localId === id ? { ...q, category: cat } : q));
  const applySharedToAll = (cat) => { setSharedCategory(cat); setQueued(prev => prev.map(q => ({ ...q, category: cat }))); };

  // ── XHR-based upload with live progress ──
  const uploadOne = useCallback((item) => {
    const { localId, file, category } = item;

    const updateRow = (patch) => setUploads(prev => {
      const existing = prev.find(u => u.localId === localId);
      if (existing) return prev.map(u => u.localId === localId ? { ...u, ...patch } : u);
      return [{ localId, filename: file.name, size: file.size, ...patch }, ...prev];
    });

    updateRow({ filename: file.name, size: file.size, status: 'uploading', progress: 0, error: null });

    const token = localStorage.getItem('access_token') ||
                  localStorage.getItem('authToken') ||
                  localStorage.getItem('token') || '';

    const baseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${baseUrl}/api/documents/upload`);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    // Real progress events
    xhr.upload.onprogress = (pe) => {
      if (!pe.lengthComputable) return;
      const pct = Math.round((pe.loaded / pe.total) * 100);
      // Keep at 99 until server responds — 100% means complete
      updateRow({ status: 'uploading', progress: Math.min(pct, 99) });
    };

    // Upload stream finished — server is now processing
    xhr.upload.onload = () => updateRow({ status: 'processing', progress: 100 });

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          updateRow({
            status: 'complete',
            progress: 100,
            docId: data?.id,
            serverStatus: data?.status || 'under_review',
            categoryLabel: data?.categoryLabel,
          });
          onUploaded?.(data);
        } catch {
          updateRow({ status: 'error', error: 'Upload failed. Please try again.' });
        }
      } else {
        let msg = 'Upload failed. Please try again.';
        try {
          const j = JSON.parse(xhr.responseText);
          if (xhr.status === 413) msg = 'File exceeds 25MB limit';
          else if (xhr.status === 400 && /type/i.test(j.detail || '')) msg = 'File type not accepted';
          else if (j.detail) msg = typeof j.detail === 'string' ? j.detail : 'Upload failed. Please try again.';
        } catch { /* keep default */ }
        updateRow({ status: 'error', error: msg });
      }
    };
    xhr.onerror    = () => updateRow({ status: 'error', error: 'Upload failed. Check connection and retry.' });
    xhr.ontimeout  = () => updateRow({ status: 'error', error: 'Upload failed. Check connection and retry.' });
    xhr.onabort    = () => updateRow({ status: 'error', error: 'Upload cancelled.' });

    const form = new FormData();
    form.append('file', file);
    form.append('category', category);
    xhr.send(form);
  }, [onUploaded]);

  const startUploads = () => {
    if (queued.length === 0) return;
    const toRun = [...queued];
    setQueued([]);
    toRun.forEach(uploadOne);
  };

  const dismissUpload = (id) => setUploads(prev => prev.filter(u => u.localId !== id));

  return (
    <div className="space-y-4" data-testid="document-upload-zone">
      <div className="panel p-3 bg-[#F0F7FF] border-blue-200" data-testid="documents-security-notice">
        <div className="flex items-start gap-2">
          <ShieldCheck size={14} className="text-blue-700 mt-0.5 shrink-0" />
          <p className="text-[11px] text-blue-900 leading-relaxed">
            Documents are encrypted using <b>AES-256</b> and retained per federal records
            requirements <span className="font-mono">(44 U.S.C. § 3301)</span>.{' '}
            Minimum retention period: <b>6 years</b>.
          </p>
        </div>
      </div>

      <div
        role="button" tabIndex={0}
        onDragEnter={onDragEnter} onDragOver={onDragOver}
        onDragLeave={onDragLeave} onDrop={onDrop}
        onClick={onClickBrowse}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClickBrowse(); } }}
        data-testid="dropzone"
        data-drag-active={dragActive ? 'true' : 'false'}
        className={
          'relative cursor-pointer rounded-lg border-2 border-dashed transition-all duration-150 ' +
          'flex flex-col items-center justify-center text-center px-6 py-10 outline-none ' +
          (dragActive
            ? 'bg-[#EEF2FF] border-[#1B2A4A] scale-[1.01] shadow-inner'
            : 'bg-white border-[#D1D5DB] hover:border-[#1B2A4A] hover:bg-[#F8F9FA] active:bg-[#F0F1F3]')
        }
      >
        <input ref={inputRef} type="file" multiple accept={ACCEPT_ATTR}
               onChange={onInputChange} className="sr-only" data-testid="file-input" />
        <div className={
          'w-12 h-12 rounded-full flex items-center justify-center mb-3 ' +
          (dragActive ? 'bg-[#1B2A4A] text-white' : 'bg-[#F0F1F3] text-[#1B2A4A]')
        }>
          <Upload size={20} />
        </div>
        <p className="text-sm font-semibold text-[#1B2A4A]" data-testid="dropzone-label">
          {dragActive ? 'Release to upload' : 'Drag & drop files here, or click to browse'}
        </p>
        <p className="text-[11px] text-[#6B7280] mt-1.5">
          PDF, JPG, PNG, DOC, DOCX · Max <b>25 MB</b> per file · Multiple files allowed
        </p>
      </div>

      {queued.length > 0 && (
        <div className="panel p-0 overflow-hidden" data-testid="queued-panel">
          <div className="flex items-center justify-between px-5 py-3 border-b border-[#E2E5E9] bg-[#FAFBFC] flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <FolderOpen size={14} className="text-[#1B2A4A]" />
              <span className="text-xs font-semibold text-[#1B2A4A] uppercase tracking-wider">
                Ready to Upload ({queued.length})
              </span>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-[#6B7280] flex items-center gap-1">
                <Tag size={11} /> Apply to all:
              </label>
              <select value={sharedCategory} onChange={e => applySharedToAll(e.target.value)}
                      className="h-8 px-2 text-xs border border-[#D1D5DB] rounded bg-white"
                      data-testid="shared-category-select">
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <button onClick={startUploads} data-testid="start-uploads-btn"
                      className="h-8 px-4 rounded-full text-xs font-medium text-white bg-[#1B2A4A] hover:bg-[#0F1A33] inline-flex items-center gap-1.5">
                <Upload size={11} /> Upload {queued.length}
              </button>
            </div>
          </div>
          <div className="divide-y divide-[#F0F1F3]">
            {queued.map(q => (
              <div key={q.localId} className="px-5 py-3 flex items-center gap-3" data-testid={`queued-${q.localId}`}>
                <FileText size={14} className="text-[#6B7280]" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-[#1B2A4A] truncate">{q.file.name}</p>
                  <p className="text-[10px] font-mono text-[#9CA3AF]">{niceSize(q.file.size)}</p>
                </div>
                <select value={q.category} onChange={e => setItemCategory(q.localId, e.target.value)}
                        className="h-8 px-2 text-[11px] border border-[#D1D5DB] rounded bg-white"
                        data-testid={`queued-category-${q.localId}`}>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <button onClick={() => removeFromQueue(q.localId)} data-testid={`remove-queued-${q.localId}`}
                        className="h-7 w-7 rounded text-[#9CA3AF] hover:text-red-600 hover:bg-red-50 inline-flex items-center justify-center">
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {uploads.length > 0 && (
        <div className="panel p-0 overflow-hidden" data-testid="uploads-status-panel">
          <div className="px-5 py-3 border-b border-[#E2E5E9] bg-[#FAFBFC]">
            <span className="text-xs font-semibold text-[#1B2A4A] uppercase tracking-wider">
              Upload Activity
            </span>
          </div>
          <div className="divide-y divide-[#F0F1F3]">
            {uploads.map(u => <UploadRow key={u.localId} u={u} onDismiss={() => dismissUpload(u.localId)} />)}
          </div>
        </div>
      )}
    </div>
  );
};


function UploadRow({ u, onDismiss }) {
  // Status-specific presentation
  let icon, color, label;
  if (u.status === 'uploading') {
    icon = <Loader2 size={13} className="animate-spin text-blue-600" />;
    color = 'text-blue-600';
    label = `Uploading ${u.progress || 0}%`;
  } else if (u.status === 'processing') {
    icon = <Cog size={13} className="animate-spin text-amber-600" />;
    color = 'text-amber-600';
    label = 'Processing…';
  } else if (u.status === 'complete') {
    icon = <CheckCircle size={13} className="text-emerald-600" />;
    color = 'text-emerald-600';
    label = 'Complete — Under Review';
  } else if (u.status === 'error') {
    icon = <AlertTriangle size={13} className="text-red-600" />;
    color = 'text-red-600';
    label = 'Failed';
  }

  return (
    <div className="px-5 py-3 flex items-center gap-3" data-testid={`upload-row-${u.localId}`}>
      <FileText size={14} className="text-[#6B7280] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-xs font-medium text-[#1B2A4A] truncate" data-testid={`upload-filename-${u.localId}`}>
            {u.filename}
          </p>
          {u.categoryLabel && (
            <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-[#1B2A4A] text-white">
              {u.categoryLabel.toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <span className={`flex items-center gap-1 text-[10px] font-semibold ${color}`} data-testid={`upload-status-${u.localId}`}>
            {icon}{label}
          </span>
          {u.size != null && <span className="text-[10px] font-mono text-[#9CA3AF]">· {niceSize(u.size)}</span>}
        </div>
        {(u.status === 'uploading' || u.status === 'processing') && (
          <div className="mt-1 w-full h-1 bg-[#F0F1F3] rounded overflow-hidden">
            <div className={'h-full transition-all ' + (u.status === 'processing' ? 'bg-amber-500' : 'bg-blue-500')}
                 style={{ width: `${u.progress || 0}%` }}
                 data-testid={`upload-progress-bar-${u.localId}`} />
          </div>
        )}
        {u.error && (
          <p className="text-[11px] text-red-700 mt-1" data-testid={`upload-error-${u.localId}`}>{u.error}</p>
        )}
      </div>
      <button onClick={onDismiss} className="h-7 w-7 rounded text-[#9CA3AF] hover:text-[#374151] hover:bg-gray-50 inline-flex items-center justify-center">
        <X size={12} />
      </button>
    </div>
  );
}

export default DocumentUploadZone;
